import { yupResolver } from "@hookform/resolvers/yup";
import { generate } from "@pdfme/generator";
import { checkbox, image, text } from "@pdfme/schemas";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Container } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import DriverRequiredFormField from "../../../components/Form/DriverRequiredFormField";
import ConfirmationModal from "../../../components/Modal/ConfirmationModal";
import config from "../../../constants/config";
import logo from "../../../assets/nsw.png";
import { doLogout, getUserData } from "../../../rtk-slice/globalSlice";
import { fetchService, loadTemplate, postFile } from "../../../utils/api";
import { errorToast, successToast } from "../../../utils/toastFunc";
import "../acnSignUp.scss";
import { FormField } from "../../../components/Form";
import { useMutation, useQuery } from "@tanstack/react-query";

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getLastWeekDates = () => {
  const today = new Date();
  const currentDay = today.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6

  // Determine how many days to subtract to get back to *last Monday*
  const daysSinceMonday = ((currentDay + 6) % 7) + 7;
  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - daysSinceMonday);

  // Last Sunday is 6 days after last Monday
  const lastSunday = new Date(lastMonday);
  lastSunday.setDate(lastMonday.getDate() + 6);

  // Following Wednesday is 3 days after last Sunday
  const nextWednesday = new Date(lastSunday);
  nextWednesday.setDate(lastSunday.getDate() + 3);

  return {
    lastMonday: formatDate(lastMonday),
    lastSunday: formatDate(lastSunday),
    nextWednesday: formatDate(nextWednesday),
  };
};

const validationSchema = Yup.object().shape({
  position: Yup.string().required("Position is required"),
  document: Yup.mixed().required("Signature is required"),
});

function AcnDriverSignUp() {
  const dispatch = useDispatch();
  const userData = useSelector(getUserData);
  const driverId = userData.userContext.userId;
  const { firstName = "", lastName = "" } = userData.userDetails;
  const [isLoading, setIsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [checkbox1, setCheckbox1] = useState(true);
  const [checkbox2, setCheckbox2] = useState(true);
  const { data: getProfileDetail } = useQuery({
    queryKey: ["getProfileDetail"],
    queryFn: async () => {
      const response = await fetchService({
        url: config.getProfileDetails,
      });
      return response.data;
    },
    enabled: !!driverId,
    refetchOnWindowFocus: false,
  });

  const submitForm = useMutation({
    mutationFn: async ({ url, formData }) => {
      const response = await postFile(url, formData);
      return response;
    },
    onError: (error) => {
      setIsLoading(false);
      errorToast({ mes: error.message || "Error Upload" });
    },
    onSuccess: (response) => {
      setIsLoading(false);
      if (response.data === "document uploaded successfully") {
        successToast({ mes: "Document Submitted" });
        dispatch(doLogout());
      } else {
        errorToast({ mes: response.message || "Error Upload" });
      }
    },
  });

  const [dates, setDates] = useState({
    lastMonday: null,
    lastSunday: null,
    nextWednesday: null,
  });

  useEffect(() => {
    const { lastMonday, lastSunday, nextWednesday } = getLastWeekDates();
    setDates({
      lastMonday,
      lastSunday,
      nextWednesday,
    });
  }, []);

  useEffect(() => {
    if (getProfileDetail && !getProfileDetail.acnGroup) {
      errorToast({
        mes: "You are not part of any ACN group. Please contact your administrator.",
      });
      dispatch(doLogout());
    }
  }, [dispatch, getProfileDetail]);

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: "onChange",
  });

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
  };

  const handleConfirmButtonClick = (action) => {
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const onSubmit = async (data) => {
    handleConfirmButtonClick(async () => {
      setIsLoading(true);
      const url =
        config.baseUrl +
        config.weeklyDocument.upload +
        "/" +
        driverId +
        "/" +
        "ACN_DOCUMENT";
      const formData = new FormData();
      const plugins = { text, image, checkbox };
      const inputs = [
        {
          fullname: `${firstName} ${lastName ?? ""}`,
          acnNumber: getProfileDetail?.acnNumber || "",
          address: getProfileDetail?.address || "",
          driverGroup: getProfileDetail?.driverGroupName || "",
          acnNumber2: getProfileDetail?.driverGroupAbn || "",
          employeeId: getProfileDetail?.employeeId || "",
          lastMonday: dates.lastMonday,
          lastSunday: dates.lastSunday,
          nextWednesday: dates.nextWednesday,
          fullname2: `${firstName} ${lastName ?? ""}`,
          checkbox1: checkbox1 ? "true" : "false",
          checkbox2: checkbox2 ? "true" : "false",
          signature: data.document,
          signatureName: `${firstName} ${lastName ?? ""}`,
          personPosition: data.position,
          signatureDate: moment().format("DD-MM-YYYY"),
        },
      ];
      const template = await loadTemplate("acnTemplate");
      // Now use `template` with your PDF generation logic
      if (!template) {
        setIsLoading(false);
        return errorToast({ mes: "Template not found" });
      }
      const pdf = await generate({ template, inputs, plugins });
      const blob = new Blob([pdf.buffer], { type: "application/pdf" });
      const file = new File([blob], "ACN_DOCUMENT.pdf", {
        type: "application/pdf",
      });
      formData.append("document", file);

      submitForm.mutate({ url, formData });

      window.open(URL.createObjectURL(blob));
    });
  };

  const handleChange = (event) => {
    if (event.target.name === "firstCheckbox") {
      setCheckbox1(event.target.checked);
    }
    if (event.target.name === "secondCheckBox") {
      setCheckbox2(event.target.checked);
    }
  };

  return (
    <>
      <div className="acn-container">
        <div className="acn-document">
          <div className="d-flex justify-content-between">
            <h2>Revenue</h2>
            <img src={logo} width={85} height={95} alt="acn" />
          </div>
          <div className="acn-border">
            <h1>
              Subcontractor’s Statement regarding Worker’s Compensation, Payroll
              Tax and Remuneration
            </h1>
            <span className="fs-6">(Note 1 – see back of form)</span>
            <p className="mt-3">
              For the purposes of this Statement a “subcontractor” is a person
              (or other legal entity) that has entered into a contract with a
              “principal contractor” to carry out work.
              <br />
              This Statement must be signed by a “subcontractor” (or by a person
              who is authorised, or held out as being authorised, to sign the
              statement by the subcontractor) referred to in any of s175B
              Workers Compensation Act 1987, Schedule 2 Part 5 Payroll Tax Act
              2007, and s127 Industrial Relations Act 1996 where the
              “subcontractor” has employed or engaged workers or subcontractors
              during the period of the contract to which the form applies under
              the relevant Act(s). The signed statement is to be submitted to
              the relevant principal contractor.
            </p>
          </div>
          <div className="mt-3">
            <h5 className="fw-bold">Subcontractor’s Statement</h5>
            <p className="letter-spacing-1">
              Refer to the back of this form for notes, period of statement
              retention, and offences under various Acts. Subcontractor: {""}
              <u>
                {firstName} {lastName}
              </u>
              {""} ABN: <u>{getProfileDetail?.acnNumber}</u> of{" "}
              <u>{getProfileDetail?.address}</u> has entered into a contract
              with <u>{getProfileDetail?.driverGroupName}</u> ABN:{" "}
              <u>{getProfileDetail?.driverGroupAbn}</u> Contract
              number/identifier <u>{getProfileDetail?.employeeId}</u> This
              statement applies for work between: <u>{dates.lastMonday}</u> and{" "}
              {""}
              <u>{dates.lastSunday}</u> inclusive, subject of the payment claim
              dated: {""}
              <u>{dates.nextWednesday}</u>
              <br />
              I,{" "}
              <u>
                {firstName} {lastName}
              </u>{" "}
              a Director or a person authorised by the Subcontractor on whose
              behalf this declaration is made, hereby declare that I am in a
              position to know the truth of the matters which are contained in
              this Subcontractor’s Statement and declare the following to the
              best of my knowledge and belief:
            </p>
            <ol type="a">
              <li>
                The above mentioned Subcontractor has either employed or engaged
                workers or subcontractors during the above period of this
                contract. Tick {""}
                <input
                  type="checkbox"
                  name="firstCheckbox"
                  checked={checkbox1}
                  onChange={handleChange}
                />{" "}
                if true and comply with (b) to (g) below, as applicable. If it
                is not the case that workers or subcontractors are involved or
                you are an exempt employer for workers compensation purposes
                tick{" "}
                <input
                  type="checkbox"
                  name="secondCheckBox"
                  checked={checkbox2}
                  onChange={handleChange}
                />{" "}
                and only complete (f) and (g) below. You must tick one box.
              </li>
              <li>
                All workers compensation insurance premiums payable by the
                Subcontractor in respect of the work done under the contract
                have been paid. The Certificate of Currency for that insurance
                is attached and is dated (DD/MM/YYYY){" "}
              </li>
              <li>
                All remuneration payable to relevant employees for work under
                the contract for the above period has been paid.
              </li>
              <li>
                Where the Subcontractor is required to be registered as an
                employer under the Payroll Tax Act 2007, the Subcontractor has
                paid all payroll tax due in respect of employees who performed
                work under the contract, as required at the date of this
                Subcontractor’s Statement.
              </li>
              <li>
                Where the Subcontractor is also a principal contractor in
                connection with the work, the Subcontractor has in its capacity
                of principal contractor been given a written Subcontractor’s
                Statement by its subcontractor(s) in connection with that work
                for the period stated above.
              </li>
            </ol>
          </div>
          <div className="mt-3">
            <h5 className="fw-bold">Notes</h5>
            <ol type="1">
              <li>
                This form is prepared for the purpose of section 175B of the
                Workers Compensation Act 1987, Schedule 2 Part 5 Payroll Tax Act
                2007 and section 127 of the Industrial Relation Act 1996. If
                this form is completed in accordance with these provisions, a
                principal contractor is relieved of liability for workers
                compensation premiums, payroll tax and remuneration payable by
                the subcontractor. A principal contractor can be generally
                defined to include any person who has entered into a contract
                for the carrying out of work by another person (or other legal
                entity called the subcontractor) and where employees of the
                subcontractor are engaged in carrying out the work which is in
                connection with the principal contractor’s business.
              </li>
              <li>
                For the purpose of this Subcontractor’s Statement, a principal
                contractor is a person (or other legal entity), who has entered
                into a contract with another person (or other legal entity)
                referred to as the subcontractor, and employees/workers of that
                subcontractor will perform the work under contract. The work
                must be connected to the business undertaking of the principal
                contractor.
              </li>
              <li>
                Provide the unique contract number, title, or other information
                that identifies the contract.
              </li>
              <li>
                In order to meet the requirements of s127 Industrial Relations
                Act 1996, a statement in relation to remuneration must state the
                period to which the statement relates. For sequential Statements
                ensure that the dates provide continuous coverage. Section
                127(6) of the Industrial Relations Act 1996 defines remuneration
                ‘as remuneration or other amounts payable to relevant employees
                by legislation, or under an industrial instrument, in connection
                with work done by the employees.’ Section 127(11) of the
                Industrial Relations Act 1996 states ‘to avoid doubt, this
                section extends to a principal contractor who is the owner or
                occupier of a building for the carrying out of work in
                connection with the building so long as the building is owned or
                occupied by the principal contractor in connection with a
                business undertaking of the principal contractor.’
              </li>
              <li>Provide the date of the most recent payment claim.</li>
              <li>
                For Workers Compensation purposes, an exempt employer is an
                employer who pays less than $7500 annually, who does not employ
                an apprentice or trainee and is not a member of a group.
              </li>
              <li>
                In completing the Subcontractor’s Statement, a subcontractor
                declares that workers compensation insurance premiums payable up
                to and including the date(s) on the Statement have been paid,
                and all premiums owing during the term of the contract will be
                paid.
              </li>
              <li>
                In completing the Subcontractor’s Statement, a subcontractor
                declares that all remuneration payable to relevant employees for
                work under the contract has been paid.
              </li>
              <li>
                In completing the Subcontractor’s Statement, a subcontractor
                declares that all payroll tax payable relating to the work
                undertaken has been paid.
              </li>
              <li>
                t is important to note that a business could be both a
                subcontractor and a principal contractor, if a business ‘in
                turn’ engages subcontractors to carry out the work. If your
                business engages a subcontractor, you are to also obtain
                Subcontractor’s Statements from your subcontractors.
              </li>
            </ol>
          </div>
          <div className="mt-3">
            <h5 className="fw-bold">Statement Retention</h5>
            <p>
              The principal contractor receiving a Subcontractor’s Statement
              must keep a copy of the Statement for the periods stated in the
              respective legislation. This is currently up to seven years
            </p>
            <div className="statement-retention">
              <h5 className="fw-bold">
                Offences in respect of a false Statement
              </h5>
              <ol>
                In terms of s127(8) of the Industrial Relations Act 1996, a
                person who gives the principal contractor a written statement
                knowing it to be false is guilty of an offence if:
                <li>the person is the subcontractor;</li>
                <li>
                  the person is authorised by the subcontractor to give the
                  statement on behalf of the subcontractor; or
                </li>
                <li>
                  the person holds out or represents that the person is
                  authorised by the subcontractor to give the statement on
                  behalf of the subcontractor.
                </li>
              </ol>
              In terms of s175B of the Workers Compensation Act and clause 18 of
              Schedule 2 of the Payroll Tax Act 2007 a person who gives the
              principal contractor a written statement knowing it to be false is
              guilty of an offence.
            </div>
          </div>
          <div className="mt-3">
            <h5 className="fw-bold"> Further Information</h5>
            <p>
              For more information, visit the SafeWork NSW website
              www.safework.nsw.gov.au, Revenue NSW website
              www.revenue.nsw.gov.au, or Office of Industrial Relations,
              Department of Commerce website www.commerce. nsw.gov.au. Copies of
              the Workers Compensation Act 1987, the Payroll Tax Act 2007 and
              the Industrial Relations Act 1996 can be found at
              www.legislation.nsw.gov.au
            </p>
            <div className="statement-retention">
              <h6 className="fw-bold">Privacy Collection Notice</h6>
              <p>
                Revenue NSW is collecting your information to administer the
                Payroll Tax Act 2007.
                <br />
                Revenue NSW gives priority to protecting the privacy of
                information we receive. We do this by handling information in a
                responsible manner and in accordance with the Privacy and
                Personal Information Protection Act 1998 and Taxation
                Administration Act 1996.
                <br />
                Revenue NSW may disclose your information for law enforcement,
                for legal proceedings arising out of a taxation law, to
                permitted statutory or regulatory bodies, for reciprocal
                taxation law administration with another Australian
                jurisdiction, or any other reason contained within Part 9,
                Division 3 of the Taxation Administration Act 1996, or as
                permitted by law. Revenue NSW does not require you to disclose
                tax file numbers for Payroll Tax purposes. If you submit
                information to us that includes tax file numbers and data that
                was not requested, you should redact this information where
                possible.
              </p>
              <hr />
              <p>
                For more privacy information regarding the collection of
                information about an individual, please refer to the Revenue NSW
                Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Container>
        <div className="eachPage mb-0">
          <form onSubmit={handleSubmit(onSubmit)}>
            <DriverRequiredFormField
              errors={errors}
              firstName={firstName}
              lastName={lastName ?? ""}
              isLoading={isLoading}
              fullName={true}
              setValue={setValue}
            >
              <Col xs={12} sm={6} md={6}>
                <FormField
                  className="Field_label"
                  title="Position"
                  id="position"
                  ratio={[3, 9]}
                >
                  <input
                    type="text"
                    {...register("position")}
                    className="form-control"
                    value={getProfileDetail?.driverTypeName || ""}
                  />
                </FormField>
              </Col>
              <Col xs={12} sm={6} md={6}>
                <FormField
                  className="Field_label"
                  title="Date"
                  id="date"
                  ratio={[2, 9]}
                >
                  {new Date().toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </FormField>
              </Col>
            </DriverRequiredFormField>
          </form>

          <div className="fs-5 mt-2">
            <span className="fw-bold">Notes: </span>
            Where required above, this Statement must be accompanied by the
            relevant Certificate of Currency to comply with section 175B of the
            Workers Compensation Act 1987.
          </div>
          <ConfirmationModal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
            onConfirm={handleConfirm}
            message="Are you sure you want to submit this file?"
          />
        </div>
      </Container>
    </>
  );
}

export default AcnDriverSignUp;
